import { StatusConstants } from "./apiStatusConstants";
import {
  formatDurationSecondsTooltip,
  formatEpochSecondsForDisplay,
  normalizeEpochSeconds,
} from "./datetime-display";

/** User Service contract (ACME-2071) — camelCase with PascalCase fallbacks at read time. */
export const ACCOUNT_LOCK_TYPE_REDIS_FAILED_LOGIN = "redisFailedLogin";

export type UserAccountLoginLockFields = {
  accountLoginLockActive: boolean;
  accountLockType: string | null;
  accountLockReason: string | null;
  accountLockStartTime: number | null;
  accountLockUnlockAt: number | null;
  accountLockRemainingSeconds: number | null;
};

/** End-user copy for sign-in, MFA, and forced-logout account-lock notices. */
export const ACCOUNT_LOGIN_LOCK_USER_MESSAGE =
  "Your account is temporarily locked due to too many failed sign-in attempts. Wait for the lock to expire before trying again (~30 minutes in production).";

function readBool(record: Record<string, unknown>, camel: string, pascal: string): boolean {
  const v = record[camel] ?? record[pascal];
  return v === true || v === "true";
}

function readString(record: Record<string, unknown>, camel: string, pascal: string): string | null {
  const v = record[camel] ?? record[pascal];
  if (v == null || v === "") {
    return null;
  }
  return String(v);
}

function readNumber(record: Record<string, unknown>, camel: string, pascal: string): number | null {
  const v = record[camel] ?? record[pascal];
  if (v == null || v === "") {
    return null;
  }
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Normalize lock fields from USER_SEARCH / GET_USER row objects. */
export function normalizeUserAccountLoginLock(
  record: Record<string, unknown> | null | undefined,
): UserAccountLoginLockFields {
  const r = record ?? {};
  return {
    accountLoginLockActive: readBool(r, "accountLoginLockActive", "AccountLoginLockActive"),
    accountLockType: readString(r, "accountLockType", "AccountLockType"),
    accountLockReason: readString(r, "accountLockReason", "AccountLockReason"),
    accountLockStartTime: readNumber(r, "accountLockStartTime", "AccountLockStartTime"),
    accountLockUnlockAt: readNumber(r, "accountLockUnlockAt", "AccountLockUnlockAt"),
    accountLockRemainingSeconds: readNumber(
      r,
      "accountLockRemainingSeconds",
      "AccountLockRemainingSeconds",
    ),
  };
}

/** Padlock only for temporary Redis failed-login lock (not inactive/disabled status). */
export function shouldShowRedisLoginLockPadlock(
  record: Record<string, unknown> | null | undefined,
): boolean {
  const lock = normalizeUserAccountLoginLock(record);
  if (!lock.accountLoginLockActive) {
    return false;
  }
  const t = (lock.accountLockType ?? "").trim();
  if (!t) {
    return false;
  }
  return t.toLowerCase() === ACCOUNT_LOCK_TYPE_REDIS_FAILED_LOGIN.toLowerCase();
}

function formatLockTimingLines(lock: UserAccountLoginLockFields): string[] {
  const lines: string[] = [];
  if (lock.accountLockStartTime != null && lock.accountLockStartTime > 0) {
    const start = normalizeEpochSeconds(lock.accountLockStartTime);
    if (start > 0) {
      lines.push(`Lock started: ${formatEpochSecondsForDisplay(start)}`);
    }
  }
  if (lock.accountLockUnlockAt != null && lock.accountLockUnlockAt > 0) {
    const unlock = normalizeEpochSeconds(lock.accountLockUnlockAt);
    if (unlock > 0) {
      lines.push(`Unlocks at: ${formatEpochSecondsForDisplay(unlock)}`);
    }
  } else if (
    lock.accountLockRemainingSeconds != null &&
    lock.accountLockRemainingSeconds > 0
  ) {
    lines.push(
      `Time remaining: ${formatDurationSecondsTooltip(lock.accountLockRemainingSeconds)}`,
    );
  }
  return lines;
}

/** Admin tooltip / helper copy for list, edit header, and auth tab. */
export function buildAccountLoginLockAdminTooltip(
  record: Record<string, unknown> | null | undefined,
): string {
  const lock = normalizeUserAccountLoginLock(record);
  const lines: string[] = [];

  if (shouldShowRedisLoginLockPadlock(record)) {
    lines.push(
      lock.accountLockReason?.trim() ||
        "Account temporarily locked due to failed sign-in attempts. Wait for the lock to expire.",
    );
  } else if (lock.accountLoginLockActive && lock.accountLockReason?.trim()) {
    lines.push(lock.accountLockReason.trim());
  } else if (lock.accountLoginLockActive && lock.accountLockType?.trim()) {
    lines.push(`Account lock: ${lock.accountLockType.trim()}`);
  }

  lines.push(...formatLockTimingLines(lock));
  return lines.filter(Boolean).join("\n");
}

function remainingSecondsFromDetail(detail: Record<string, unknown>): number | null {
  return readNumber(detail, "accountLockRemainingSeconds", "AccountLockRemainingSeconds");
}

/** True when any API detail carries the account-lock status code. */
export function hasAuthAccountLockError(
  details: Array<{ code?: string }> | null | undefined,
): boolean {
  if (!details?.length) {
    return false;
  }
  return details.some((d) => d?.code === StatusConstants.ACC_LOCK_CODE);
}

/** Sign-in / MFA errors: map ACC_LOCK_CODE to catalog copy; prefer API description when present. */
export function getAuthAccountLockErrorMessage(
  details: Array<{ code?: string; description?: string } & Record<string, unknown>> | null | undefined,
): string {
  if (!details?.length) {
    return "";
  }
  if (!hasAuthAccountLockError(details)) {
    return "";
  }
  const lockDetail = details.find(
    (d) => d?.code === StatusConstants.ACC_LOCK_CODE,
  )!;
  const desc = (lockDetail.description ?? "").trim();
  let msg = desc || ACCOUNT_LOGIN_LOCK_USER_MESSAGE;
  const remaining = remainingSecondsFromDetail(lockDetail);
  if (remaining != null && remaining > 0) {
    msg += ` Time remaining: ${formatDurationSecondsTooltip(remaining)}.`;
  }
  return msg;
}

/** Auth responses: lock catalog when coded; otherwise default getErrorMessage behavior. */
export function resolveAuthFlowErrorMessage(
  details: Array<{ code?: string; description?: string } & Record<string, unknown>> | null | undefined,
  fallbackGetErrorMessage: (errors: unknown) => string,
): string {
  const lockMsg = getAuthAccountLockErrorMessage(details);
  if (lockMsg) {
    return lockMsg;
  }
  return fallbackGetErrorMessage(details);
}
